import React, { type ComponentProps, useCallback } from 'react';
import { useIntl } from 'react-intl';
import { useNoticeBannersStackContext, useToggleState } from '@sg/garnish';

import { ReorderConflictModal } from '@order/components';
import { OrderLineItem } from '@order/features/orders';
import {
  useNavigateToReorderProductDetails,
  useReorderOrderLineItem,
} from '@order/hooks';
import { useTelemetry } from '@order/Telemetry';

import { reorderOrderLineItemMessages as messages } from './ReorderOrderLineItem.messages';

// ─────────────────────────────────────────────────────────────────────────────

export const ReorderOrderLineItem = (props: ReorderOrderLineItemProps) => {
  const {
    variation,
    lineItem,
    orderDetails,
    mostRecentOrder,
    shouldUseModalNavigation,
    shouldShowSuccessBanner,
    shouldReplaceScreen,
  } = props;

  const { push: addBanner } = useNoticeBannersStackContext();
  const { formatMessage } = useIntl();
  const { track } = useTelemetry();

  // ─── State ───────────────────────────────────────────────────────────

  const {
    value: isConflictReviewModalVisible,
    toggleOn: showConflictReviewModal,
    toggleOff: hideConflictReviewModal,
  } = useToggleState(false);

  // ─── Helpers ─────────────────────────────────────────────────────────

  const navigateToProductDetailsScreen = useNavigateToReorderProductDetails({
    shouldUseModalNavigation,
    shouldReplaceScreen,
  });

  const onSuccessfulReorder = useCallback(() => {
    hideConflictReviewModal();

    if (!shouldShowSuccessBanner) return;

    addBanner({
      palette: 'success',
      text: formatMessage(messages.successfulReorderMessage, {
        quantity: 1,
        product: lineItem.product.name,
      }),
      autoHideTimeout: 2500,
    });
  }, [
    addBanner,
    formatMessage,
    lineItem.product.name,
    shouldShowSuccessBanner,
    hideConflictReviewModal,
  ]);

  const {
    reorderLineItem,
    isFetching: isReordering,
    unavailableIngredients,
    targetLocation,
  } = useReorderOrderLineItem({
    variation: 'order',
    mostRecentOrder,
    productName: lineItem.product.name,
    onUnavailableIngredientConflict: showConflictReviewModal,
    onSuccess: onSuccessfulReorder,
  });

  const handleNavigateToProductDetailsScreen =
    useCallback<OnProductDetailsLinkPress>(
      (navigationParams) => {
        hideConflictReviewModal();

        track('reorder.customize.tapped');

        navigateToProductDetailsScreen(navigationParams);
      },
      [hideConflictReviewModal, navigateToProductDetailsScreen, track],
    );

  // ─── Derived Data ────────────────────────────────────────────────────

  const reorderLineItemForce = useCallback(async () => {
    await reorderLineItem(lineItem.id, true);
  }, [lineItem.id, reorderLineItem]);

  // ─────────────────────────────────────────────────────────────────────

  return (
    <>
      <OrderLineItem
        variation={variation}
        lineItem={lineItem}
        orderDetails={orderDetails}
        isReordering={isReordering}
        onProductDetailsLinkPress={handleNavigateToProductDetailsScreen}
        onAddToBagButtonPress={reorderLineItem}
      />

      <ReorderConflictModal
        visible={isConflictReviewModalVisible}
        lineItem={lineItem}
        isReordering={isReordering}
        onRequestClose={hideConflictReviewModal}
        navigateToProductDetails={handleNavigateToProductDetailsScreen}
        addToBagAnyway={reorderLineItemForce}
        targetLocation={targetLocation}
        unavailableIngredients={unavailableIngredients}
      />
    </>
  );
};

// ─── Types ───────────────────────────────────────────────────────────────────

type ReorderOrderLineItemProps = {
  lineItem: OrderLineItemProps['lineItem'];
  orderDetails: OrderLineItemProps['orderDetails'];
  mostRecentOrder: UseReorderOrderLineItemParams['mostRecentOrder'];
  variation?: OrderLineItemProps['variation'];
  shouldUseModalNavigation: boolean;
  shouldShowSuccessBanner?: boolean;
  shouldReplaceScreen?: boolean;
};

type OrderLineItemProps = ComponentProps<typeof OrderLineItem>;

type UseReorderOrderLineItemParams = Parameters<
  typeof useReorderOrderLineItem
>[0];

type OnProductDetailsLinkPress = ComponentProps<
  typeof OrderLineItem
>['onProductDetailsLinkPress'];
